import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  Loading,
  Modal,
  PageHeader,
  Select,
  Toast,
} from '@/components/ui';
import { formatDate, titleCase } from '@/lib/format';
import { SttTest } from '@/components/SttTest';

interface Provider {
  id: string;
  kind: string;
  key: string;
  name: string;
  driver: string;
  config: Record<string, unknown>;
  enabled: boolean;
  is_default: boolean;
  health_state: string;
  health_error?: string | null;
  has_credentials: boolean;
  /** The saved key's vendor prefix and last four characters -- never the key. */
  key_hint?: string | null;
}

interface Rate {
  id: string;
  provider_id: string;
  provider_name: string;
  kind: string;
  usage_kind: string;
  model: string | null;
  unit: string;
  unit_cost: string;
  currency: string;
  effective_from: string;
  effective_to: string | null;
}

const USAGE_KINDS = [
  'LLM_INPUT_TOKENS',
  'LLM_OUTPUT_TOKENS',
  'STT_SECONDS',
  'TTS_CHARACTERS',
  'TELEPHONY_MINUTES',
  'OTHER',
];

export default function Providers() {
  const queryClient = useQueryClient();
  const [credentialFor, setCredentialFor] = useState<Provider | null>(null);
  const [testing, setTesting] = useState<Provider | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [rateOpen, setRateOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const providers = useQuery({
    queryKey: ['admin-providers'],
    queryFn: () => api.get<{ data: Provider[] }>('/admin/providers'),
  });
  const rates = useQuery({
    queryKey: ['admin-rates'],
    queryFn: () => api.get<{ data: Rate[] }>('/admin/provider-rates'),
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      api.patch(`/admin/providers/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-providers'] });
      setCredentialFor(null);
      setApiKey('');
      setToast({ message: 'Provider updated', tone: 'good' });
    },
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Could not update'),
        tone: 'bad',
      }),
  });

  if (providers.isPending || rates.isPending) return <Loading />;
  if (providers.error)
    return <ErrorState error={providers.error} retry={() => providers.refetch()} />;

  // Mock drivers are the platform's own test doubles (demo mode, the test
  // suite). They are not vendors anybody chooses, and listing them beside
  // Deepgram and Anthropic made them look like options.
  const byKind = providers.data.data
    .filter((provider) => provider.driver !== 'mock')
    .reduce<Record<string, Provider[]>>((acc, provider) => {
      (acc[provider.kind] ??= []).push(provider);
      return acc;
    }, {});

  const ratesWithoutValue = rates.data?.data.filter((r) => Number(r.unit_cost) === 0) ?? [];

  return (
    <div className="w-full">
      <PageHeader
        title="Providers & rates"
        description="The adapters behind the voice pipeline, and what each one costs you."
        actions={<Button onClick={() => setRateOpen(true)}>Add rate</Button>}
      />

      {ratesWithoutValue.length > 0 && (
        <div className="mb-4 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900">
          <strong>{ratesWithoutValue.length}</strong> rate
          {ratesWithoutValue.length === 1 ? ' has' : 's have'} a unit cost of zero. Usage
          against {ratesWithoutValue.length === 1 ? 'it' : 'them'} bills the client nothing
          and silently erodes margin.
        </div>
      )}

      <div className="space-y-4">
        {Object.entries(byKind).map(([kind, list]) => (
          <Card key={kind} title={`${titleCase(kind)} providers`} padded={false}>
            <ul className="divide-y divide-line">
              {list.map((provider) => (
                <li key={provider.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-ink-900 dark:text-ink-50">
                        {provider.name}
                      </span>
                      {provider.is_default && <Badge tone="blue">Default</Badge>}
                      <Badge tone={provider.enabled ? 'green' : 'gray'}>
                        {provider.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                      {provider.has_credentials ? (
                        <Badge tone="green">Credentials set</Badge>
                      ) : provider.driver !== 'mock' && provider.driver !== 'glee_local' ? (
                        <Badge tone="amber">No credentials</Badge>
                      ) : null}
                    </div>
                    <div className="font-mono text-xs text-ink-500">
                      {provider.key} · driver {provider.driver}
                      {typeof provider.config?.model === 'string' &&
                        ` · ${provider.config.model}`}
                    </div>
                    {provider.key_hint && (
                      <div className="mt-1 text-xs text-ink-600 dark:text-ink-300">
                        Key in use{' '}
                        <span className="rounded bg-sunken px-1.5 py-0.5 font-mono text-ink-800 dark:text-ink-100">
                          {provider.key_hint}
                        </span>
                      </div>
                    )}
                    {provider.health_state === 'FAILED' && provider.health_error && (
                      <div className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">
                        {provider.health_error}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {provider.kind === 'STT' && (
                      <Button size="sm" variant="secondary" onClick={() => setTesting(provider)}>
                        Test
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setCredentialFor(provider)}
                    >
                      Credentials
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        update.mutate({
                          id: provider.id,
                          payload: { enabled: !provider.enabled },
                        })
                      }
                    >
                      {provider.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    {!provider.is_default && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          update.mutate({ id: provider.id, payload: { is_default: true } })
                        }
                      >
                        Make default
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>

      <Card
        title="Provider rates"
        description="What YOU pay per unit. Client price is derived from this by the tenant's multiplier."
        className="mt-6"
        padded={false}
      >
        <div className="overflow-x-auto">
          <table className="glee-table">
            <thead>
              <tr>
                {['Provider', 'Usage kind', 'Model', 'Unit', 'Unit cost', 'Effective from'].map(
                  (header, index) => (
                    <th
                      key={header}
                      className={`${
                        index === 4 ? 'text-right' : 'text-left'
                      }`}
                    >
                      {header}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {(rates.data?.data ?? []).map((rate) => (
                <tr key={rate.id}>
                  <td>{rate.provider_name}</td>
                  <td className="text-xs">{rate.usage_kind.replace(/_/g, ' ')}</td>
                  <td className="font-mono text-xs text-ink-500">
                    {rate.model ?? 'any'}
                  </td>
                  <td className="text-xs">{rate.unit}</td>
                  <td
                    className={`text-right font-mono text-xs tabular-nums ${
                      Number(rate.unit_cost) === 0 ? 'text-amber-600' : ''
                    }`}
                  >
                    {Number(rate.unit_cost).toFixed(10)}
                  </td>
                  <td className="text-xs text-ink-500">
                    {formatDate(rate.effective_from)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        open={credentialFor !== null}
        onClose={() => {
          setCredentialFor(null);
          setApiKey('');
        }}
        title={`Credentials — ${credentialFor?.name ?? ''}`}
        description="Stored encrypted with AES-256-GCM. Never returned by the API, and redacted from logs."
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setCredentialFor(null);
                setApiKey('');
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!apiKey}
              loading={update.isPending}
              onClick={() =>
                credentialFor &&
                update.mutate({ id: credentialFor.id, payload: { api_key: apiKey } })
              }
            >
              Save credentials
            </Button>
          </>
        }
      >
        <Field
          label="API key"
          hint="Replacing the key drops the in-process provider cache, so it takes effect on the next call."
        >
          <Input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            autoComplete="off"
          />
        </Field>
      </Modal>

      {rateOpen && (
        <AddRateModal
          providers={providers.data.data}
          onClose={() => setRateOpen(false)}
          onDone={() => {
            setRateOpen(false);
            queryClient.invalidateQueries({ queryKey: ['admin-rates'] });
            setToast({ message: 'Rate added', tone: 'good' });
          }}
          onError={(message) => setToast({ message, tone: 'bad' })}
        />
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
      <SttTest provider={testing} onClose={() => setTesting(null)} />
    </div>
  );
}

function AddRateModal({
  providers,
  onClose,
  onDone,
  onError,
}: {
  providers: Provider[];
  onClose: () => void;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [providerId, setProviderId] = useState('');
  const [usageKind, setUsageKind] = useState('LLM_INPUT_TOKENS');
  const [model, setModel] = useState('');
  const [unit, setUnit] = useState('token');
  const [unitCost, setUnitCost] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.post('/admin/provider-rates', {
        provider_id: providerId,
        usage_kind: usageKind,
        model: model || null,
        unit,
        unit_cost: Number(unitCost),
      }),
    onSuccess: onDone,
    onError: (err) => onError(errorMessage(err, 'Could not add rate')),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Add a provider rate"
      description="Cost per single unit, in USD. A new rate supersedes the old one from now on."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!providerId || !unitCost}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Add rate
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Provider" required>
          <Select value={providerId} onChange={(e) => setProviderId(e.target.value)}>
            <option value="">Select…</option>
            {providers.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.name} ({provider.kind})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Usage kind" required>
          <Select value={usageKind} onChange={(e) => setUsageKind(e.target.value)}>
            {USAGE_KINDS.map((value) => (
              <option key={value} value={value}>
                {value.replace(/_/g, ' ')}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Model" hint="Blank matches any model for this provider.">
          <Input
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="claude-opus-5"
          />
        </Field>
        <Field label="Unit" required hint="token, second, character, minute, call">
          <Input value={unit} onChange={(e) => setUnit(e.target.value)} />
        </Field>
        <Field
          label="Unit cost (USD)"
          required
          hint="Per single unit. $5 per million tokens is 0.000005."
        >
          <Input
            type="number"
            step="0.0000000001"
            value={unitCost}
            onChange={(e) => setUnitCost(e.target.value)}
            placeholder="0.000005"
          />
        </Field>
      </div>
    </Modal>
  );
}
